import { AxiosRequestConfig } from "axios";
import {
  HTTP_HEADER_ACCEPT,
  HTTP_HEADER_CONTENT_TYPE,
  HTTP_MEDIA_TYPE_JSON,
} from "./constants";
import { DecoratedHttpClient } from "./decorated-http-client";
import { HttpClient } from "./types";

/**
 * This is the simplest class that implements a very thin abstraction on top of an
 * Axios instance. It essentially just sets the right `Accept` and `Content-Type`
 * headers for the outgoing JSON based requests.
 *
 * @example
 *
 * ```typescript
 * import { JsonHttpClient } from "@ftdr/http-utils";
 *
 * const jsonHttpClient = new JsonHttpClient();
 *
 * try {
 *   const response = await jsonHttpClient.get(
 *     "https://api.example.com/entities/1"
 *   );
 *
 *   console.log(response.data);
 * } catch (error) {
 *   console.error("Api call failed with: ", error.message);
 * }
 * ```
 */
export class JsonHttpClient<
  C extends AxiosRequestConfig = AxiosRequestConfig
> extends DecoratedHttpClient<C> {
  defaults = {
    headers: {
      common: {
        [HTTP_HEADER_ACCEPT]: HTTP_MEDIA_TYPE_JSON,
      },
      post: {
        [HTTP_HEADER_CONTENT_TYPE]: HTTP_MEDIA_TYPE_JSON,
      },
      put: {
        [HTTP_HEADER_CONTENT_TYPE]: HTTP_MEDIA_TYPE_JSON,
      },
      patch: {
        [HTTP_HEADER_CONTENT_TYPE]: HTTP_MEDIA_TYPE_JSON,
      },
    },
  };

  /**
   * Construct a new `JsonHttpClient`
   *
   * @param httpClient An optional HttpClient instance. Use this to compose
   *                   functionality of multiple HttpClient instances into a
   *                   single instance.
   */
  constructor(httpClient?: HttpClient) {
    super(httpClient);
    this.mergeDefaults();
  }
}
